import {
  BellOutlined,
  MailOutlined,
  MobileOutlined,
  SendOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Select, Tag } from 'antd';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useLazyGetCustomersQuery } from '@/modules/customers/customersApi';
import { useSendCustomerNotificationMutation } from '@/modules/notifications/notificationsApi';
import type { Customer } from '@/types/customer';
import type { SendCustomerNotificationResult } from '@/types/notification';
import { resolveMutationResult } from '@/utils/apiResponse';
import { ucfirst } from '@/utils/string';
import styles from './Notifications.module.scss';

type NotificationChannel = 'email' | 'push';

interface ComposeValues {
  customerIds: string[];
  title: string;
  message: string;
  channels: NotificationChannel[];
}

const CHANNEL_OPTIONS: { value: NotificationChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'push', label: 'Push' },
];

function renderChannelOption(option: { value?: string | number; label?: ReactNode }) {
  const icon =
    option.value === 'email' ? (
      <MailOutlined />
    ) : option.value === 'push' ? (
      <MobileOutlined />
    ) : null;

  return (
    <span className={styles.channelOption}>
      {icon}
      {option.label}
    </span>
  );
}

function formatCustomerLabel(customer: Customer) {
  const name = ucfirst(customer.name);
  return `${name} · ${customer.email}`;
}

export default function Notifications() {
  const notify = useNotify();
  const [form] = Form.useForm<ComposeValues>();
  const [selectedCustomers, setSelectedCustomers] = useState<Customer[]>([]);

  const customerIds = Form.useWatch('customerIds', form) ?? [];
  const channels = Form.useWatch('channels', form) ?? [];

  const [fetchCustomers, { data: customersResult, isFetching: customersLoading }] =
    useLazyGetCustomersQuery();
  const [sendNotification, { isLoading: isSending }] = useSendCustomerNotificationMutation();

  useGlobalLoader(isSending);

  const loadCustomers = useCallback(
    (search?: string) => {
      void fetchCustomers({
        page: 1,
        limit: 100,
        status: 'active',
        sortBy: 'name',
        sortOrder: 'asc',
        ...(search?.trim() ? { search: search.trim() } : {}),
      });
    },
    [fetchCustomers],
  );

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const customerOptions = useMemo(() => {
    const fetched = customersResult?.results ?? [];
    const merged = new Map<string, Customer>();

    for (const customer of [...selectedCustomers, ...fetched]) {
      merged.set(customer.id, customer);
    }

    return Array.from(merged.values()).map((customer) => ({
      value: customer.id,
      label: formatCustomerLabel(customer),
      customer,
    }));
  }, [customersResult?.results, selectedCustomers]);

  const handleCustomerChange = (ids: string[]) => {
    const nextSelected = ids
      .map((id) => customerOptions.find((option) => option.value === id)?.customer)
      .filter((customer): customer is Customer => Boolean(customer));

    setSelectedCustomers(nextSelected);
  };

  const handleSend = async (values: ComposeValues) => {
    if (!values.customerIds.length) {
      notify.error('Select at least one active customer.');
      return;
    }

    if (!values.channels.length) {
      notify.error('Select at least one channel: email or push.');
      return;
    }

    const sendEmail = values.channels.includes('email');
    const sendPush = values.channels.includes('push');

    const result = await resolveMutationResult<SendCustomerNotificationResult>(
      sendNotification({
        customerIds: values.customerIds,
        title: values.title.trim(),
        message: values.message.trim(),
        sendEmail,
        sendPush,
      }),
    );

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    notify.success(result.message ?? 'Notification sent successfully.');
  };

  const canSend = customerIds.length > 0 && channels.length > 0;

  return (
    <div className={styles.page}>
      <PageHeader />

      <div className={styles.composeCard}>
        <div className={styles.composeHeader}>
          <BellOutlined />
          <div>
            <h2 className={styles.composeTitle}>Send notification</h2>
            <p className={styles.composeDesc}>
              Choose active customers, then send a message by email and/or push.
            </p>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ customerIds: [], channels: ['email'] as NotificationChannel[] }}
          onFinish={handleSend}
          requiredMark={false}
        >
          <Form.Item
            label="Customers"
            name="customerIds"
            rules={[{ required: true, message: 'Select at least one customer.' }]}
          >
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="Select active customers"
              options={customerOptions}
              loading={customersLoading}
              filterOption={false}
              onOpenChange={(open) => {
                if (open) loadCustomers();
              }}
              onSearch={loadCustomers}
              onChange={handleCustomerChange}
              maxTagCount="responsive"
              suffixIcon={<UserOutlined />}
              notFoundContent={
                customersLoading ? 'Loading customers...' : 'No active customers found'
              }
              optionRender={(option) => {
                const customer = customerOptions.find(
                  (item) => item.value === option.value,
                )?.customer;
                if (!customer) return option.label;

                return (
                  <div className={styles.customerOption}>
                    <span className={styles.customerOptionName}>{ucfirst(customer.name)}</span>
                    <span className={styles.customerOptionMeta}>{customer.email}</span>
                    <span className={styles.customerOptionTags}>
                      <Tag>Email</Tag>
                    </span>
                  </div>
                );
              }}
            />
          </Form.Item>

          <div className={styles.composeGrid}>
            <Form.Item
              label="Title"
              name="title"
              rules={[{ required: true, message: 'Title is required.' }]}
            >
              <Input placeholder="Notification title" maxLength={200} />
            </Form.Item>

            <Form.Item
              label="Channels"
              name="channels"
              className={styles.channels}
              rules={[{ required: true, message: 'Select at least one channel.' }]}
            >
              <Select
                mode="multiple"
                placeholder="Select channels"
                options={CHANNEL_OPTIONS}
                optionRender={renderChannelOption}
                maxTagCount="responsive"
              />
            </Form.Item>
          </div>

          <Form.Item
            label="Message"
            name="message"
            rules={[{ required: true, message: 'Message is required.' }]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Write your message..."
              maxLength={2000}
              showCount
            />
          </Form.Item>

          <div className={styles.composeActions}>
            <span className={styles.selectionHint}>
              {customerIds.length
                ? `${customerIds.length} customer${customerIds.length === 1 ? '' : 's'} selected`
                : 'Select customers to enable sending'}
            </span>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SendOutlined />}
              loading={isSending}
              disabled={!canSend}
            >
              Send notification
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
